import * as Sentry from '@sentry/react';

import { AnyAction } from 'redux';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import {
  AUTHENTICATION_LOGOUT_START,
  AUTHENTICATION_LOGOUT_SUCCESS,
  AUTHENTICATION_SUCCESS,
  AuthState,
  clearCredentials,
  authInitialState as defaultInitialState,
  auth as defaultReducer,
  getRefreshTokenStorageKey,
} from '@tmapy/auth';

import type { AccessPolicies } from '../../lib/acl';
import type { UserRoleResponse } from '../../openapi/types';

import { clearTwistCredentials } from '../actions';
import { AUTH_LOGOUT, AUTH_SET_POLICIES, AUTH_USER_FAILURE, AUTH_USER_SUCCESS } from '../constants';

export const authInitialState = {
  ...defaultInitialState,
  version: 2,
  error: null,
  isAuthenticated: false,
  id: null as number | null,
  name: '',
  surname: '',
  roles: [] as UserRoleResponse[],
  policies: {},
  url: '',
  orp_id: null as number | null,
  orp_name: null as string | null,
} as const;

declare module '@tmapy/auth' {
  interface AuthState {
    /**
     * localStorage format version
     */
    version?: number;
    isAuthenticated: boolean;
    id: number | null;
    name: string;
    surname: string;
    roles: UserRoleResponse[];
    policies: AccessPolicies;
    url: string;
    orp_id: number | null;
    orp_name: string | null;
    error: string | null;
  }
}

const authReducer = (state: AuthState = authInitialState, action: AnyAction): AuthState => {
  switch (action.type) {
    case AUTH_USER_SUCCESS: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { token, access, refresh, ...userWithoutTokens } = action.data;
      return {
        ...state,
        ...userWithoutTokens,
        isAuthenticated: true,
      };
    }

    case AUTHENTICATION_LOGOUT_START:
    case AUTH_LOGOUT: {
      return {
        ...authInitialState,
        policies: state.policies,
      };
    }

    case AUTH_SET_POLICIES: {
      return {
        ...state,
        policies: action.payload,
      };
    }

    case AUTH_USER_FAILURE: {
      return {
        ...authInitialState,
        policies: state.policies,
        access_token: state.access_token,
        error: action.payload || 'Authentication Failed',
      };
    }

    case AUTHENTICATION_SUCCESS: {
      return {
        ...defaultReducer(state, action),
        isAuthenticated: true,
      };
    }
    case AUTHENTICATION_LOGOUT_SUCCESS: {
      return {
        ...defaultReducer(state, action),
        isAuthenticated: false,
      };
    }

    default: {
      return defaultReducer(state, action);
    }
  }
};

function isValidAuthState(data: any): data is Partial<AuthState> & Pick<AuthState, 'roles'> {
  if (typeof data !== 'object' || !data) return false;
  if (data.version !== authInitialState.version) return false;
  return Array.isArray(data.roles);
}

export const auth = persistReducer<AuthState>(
  {
    key: 'dmx.auth',
    storage,
    stateReconciler: (inboundState: unknown): AuthState => {
      // don't use invalid (outdated) state
      if (!isValidAuthState(inboundState)) {
        console.warn('[AUTH]: Invalid persisted state, cleaning localStorage');
        localStorage.clear();

        // not an action, called for side effect
        if (globalThis.DMX_CONFIG.auth) {
          clearCredentials();
        } else {
          clearTwistCredentials();
        }
        return authInitialState;
      }
      if (globalThis.DMX_CONFIG.auth && !window.localStorage.getItem(getRefreshTokenStorageKey())) {
        return authInitialState;
      }

      Sentry.setUser(inboundState as any);

      return {
        ...authInitialState,
        ...inboundState,
      };
    },
  },
  authReducer,
);
